import { CONTACT_REQUIREMENT_LABELS, type ResearchConfigInput } from '@shared/config';
import { SOURCE_DESCRIPTORS } from '@shared/sources';
import { Alert } from '../../components/ui/feedback';

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid gap-1 border-b border-border py-2 last:border-b-0 sm:grid-cols-[180px_minmax(0,1fr)] sm:gap-3">
      <dt className="text-[13px] text-muted-foreground">{label}</dt>
      <dd className="text-[13px] text-foreground">{children}</dd>
    </div>
  );
}

export interface StepReviewProps {
  config: ResearchConfigInput;
}

/** Step 5. Everything the run will do, stated plainly before anything is spent. */
export function StepReview({ config }: StepReviewProps) {
  const sources = (config.sources ?? []);
  const keywords = config.search?.keywords ?? [];
  const locations = config.search?.locations ?? [];
  const selected = config.attributes?.selected ?? [];
  const custom = config.attributes?.custom ?? [];
  const target = config.search?.targetPerSource ?? 250;
  const combinations = Math.max(1, keywords.length) * Math.max(1, locations.length);

  return (
    <div className="space-y-4">
      <dl className="rounded-[var(--radius-panel)] border border-border bg-surface px-3 py-1">
        <Row label="Name">{config.name}</Row>
        <Row label="Sources">{sources.map((id) => SOURCE_DESCRIPTORS[id].label).join(', ')}</Row>
        <Row label="Keywords">{keywords.length > 0 ? keywords.join(', ') : <span className="text-muted-foreground">None</span>}</Row>
        <Row label="Locations">
          {locations.length > 0 ? (
            locations.map((location) => [location.area, location.city, location.country].filter(Boolean).join(', ')).join(' · ')
          ) : (
            <span className="text-muted-foreground">None</span>
          )}
        </Row>
        <Row label="Searches">
          <span className="tabular">{combinations}</span> keyword × location combination{combinations === 1 ? '' : 's'}, up to{' '}
          <span className="tabular">{target.toLocaleString()}</span> results per source
        </Row>
        <Row label="Attributes">
          <span className="tabular">{selected.filter((key) => !key.startsWith('custom.')).length}</span> from the catalog
          {custom.length > 0 ? (
            <>
              {' + '}
              <span className="tabular">{custom.length}</span> custom ({custom.map((attribute) => attribute.name).join(', ')})
            </>
          ) : null}
        </Row>
        <Row label="Contact requirement">{CONTACT_REQUIREMENT_LABELS[config.rules?.contactRequirement ?? 'phone_or_email']}</Row>
        <Row label="Website enrichment">
          {config.enrichment?.website?.enabled ?? true
            ? `On, up to ${String(config.enrichment?.website?.maxPagesPerSite ?? 6)} pages per site`
            : 'Off'}
        </Row>
        <Row label="People discovery">{config.enrichment?.people?.enabled ? 'On' : 'Off'}</Row>
      </dl>

      <Alert tone="info" title="What the results will and will not be">
        This run reports the businesses discoverable through the sources and queries above. That is a market observation, not a
        complete census of the market — listings that no selected source carries will not appear.
      </Alert>
    </div>
  );
}
